// Core
import React from 'react';

// Libraries
import { useTranslation } from 'react-i18next';
import _ from 'lodash';

// Plugins
import {
  convertLength,
  DASHBOARD_NULLISH_TEXT,
  formatNumber,
  formatValueWithUnit,
  roundNumber,
} from '../../../../../../../../plugins/general';
import {
  booleanStatusToTagType,
  booleanToOnOffText,
} from '../../../../../../../../plugins/crusher';

// Store
import {
  getMeasurementUnit,
} from '../../../../../../../../store/measurement.store';

// Types
import {
  LengthType,
  MeasurementType,
} from '../../../../../../../../types/global';

// Components
import { EquipmentSensorListItem } from '../../../../../../../../components/EquipmentSensorList';
import { Tag } from '../../../../../../../../layout/elements/Tag';
// Module
import { ListModule } from '../../../ListModule';

// View
import { AutomationProps } from './Automation.types';
import { Container, ListCol, ListContainer } from './Automation.style';

function Automation({
  className,
  unitData,
  edgeStatus,
  testId,
}: AutomationProps): JSX.Element {
  const { t } = useTranslation();

  const lengthUnit: LengthType = getMeasurementUnit(MeasurementType.length);

  const autoBowlItems: EquipmentSensorListItem[] = [
    {
      isBold: true,
      sensor: t('view.crusher.maintenance.setPoint.automation.highVibrationOpenDescription'),
      setpoint: (
        <Tag
          text={booleanToOnOffText(unitData?.spAutoBowlAdjustVibrationEnable)}
          type={booleanStatusToTagType(
            unitData?.spAutoBowlAdjustVibrationEnable,
            edgeStatus?.edgeStatus ? edgeStatus?.edgeStatus : null,
            unitData?.timestamp,
          )}
        />
      ),
    },
    {
      hasLargePadding: true,
      sensor: t('view.crusher.maintenance.setPoint.automation.cssAdjustOpenDescription'),
      setpoint: unitData?.spVibAlarmTeethToOpen ? `${formatNumber(roundNumber(
        unitData?.spVibAlarmTeethToOpen,
      ))} ${t('common.teeth')}` : DASHBOARD_NULLISH_TEXT,
    },
    {
      hasLargePadding: true,
      sensor: t('view.crusher.maintenance.setPoint.automation.countForFeedDescription'),
      setpoint: unitData?.spVibCountFeedStoppage ? `${formatNumber(roundNumber(
        unitData?.spVibCountFeedStoppage,
      ))} ${t('common.events')}` : DASHBOARD_NULLISH_TEXT,
    },
    {
      hasLargePadding: true,
      sensor: t('view.crusher.maintenance.setPoint.automation.vibTimeWindowDescription'),
      setpoint: formatValueWithUnit(unitData?.spVibShiftTimeHours, 'Hrs'),
    },
    {
      isBold: true,
      sensor: t('view.crusher.maintenance.setPoint.automation.highAmpsOpenDescription'),
      setpoint: (
        <Tag
          text={booleanToOnOffText(unitData?.spAmpAdjustHighAmpsEnable)}
          type={booleanStatusToTagType(
            unitData?.spAmpAdjustHighAmpsEnable,
            edgeStatus?.edgeStatus ? edgeStatus?.edgeStatus : null,
            unitData?.timestamp,
          )}
        />
      ),
    },
    {
      hasLargePadding: true,
      sensor: t('view.crusher.maintenance.setPoint.automation.cssAdjustOpenDescription'),
      setpoint: unitData?.spAmpOpenAlarmTeeth ? `${formatNumber(roundNumber(
        unitData?.spAmpOpenAlarmTeeth,
      ))} ${t('common.teeth')}` : DASHBOARD_NULLISH_TEXT,
    },
    {
      hasLargePadding: true,
      sensor: t('view.crusher.maintenance.setPoint.automation.countForFeedDescription'),
      setpoint: unitData?.spAmpCountFeedStoppage ? `${formatNumber(roundNumber(
        unitData?.spAmpCountFeedStoppage,
      ))} ${t('common.events')}` : DASHBOARD_NULLISH_TEXT,
    },
    {
      hasLargePadding: true,
      sensor: t('view.crusher.maintenance.setPoint.automation.ampTimeWindowDescription'),
      setpoint: formatValueWithUnit(unitData?.spAmpShiftTimeHours, 'Hrs'),
    },
    {
      isBold: true,
      sensor: t('view.crusher.maintenance.setPoint.automation.lowAmpsClose'),
      setpoint: (
        <Tag
          text={booleanToOnOffText(unitData?.spAutoBowlAdjustLowAmpsEnable)}
          type={booleanStatusToTagType(
            unitData?.spAutoBowlAdjustLowAmpsEnable,
            edgeStatus?.edgeStatus ? edgeStatus?.edgeStatus : null,
            unitData?.timestamp,
          )}
        />
      ),
    },
    {
      hasLargePadding: true,
      sensor: t('view.crusher.maintenance.setPoint.automation.cssCloseAmountDescription'),
      setpoint: unitData?.spAmpCloseAlarmTeeth ? `${formatNumber(roundNumber(
        unitData?.spAmpCloseAlarmTeeth,
      ))} ${t('common.teeth')}` : DASHBOARD_NULLISH_TEXT,
    },
    {
      hasLargePadding: true,
      sensor: t('view.crusher.maintenance.setPoint.automation.minAdjustCloseCssDescription'),
      setpoint: convertLength(unitData?.spAutoCloseMinCss, lengthUnit, 2, true).formattedValue,
    },
  ];

  const autoFeederItems: EquipmentSensorListItem[] = [
    {
      isBold: true,
      sensor: t('view.crusher.maintenance.setPoint.automation.HybridLevelDescription'),
      setpoint: (
        <Tag
          text={booleanToOnOffText(
            _.isNil(unitData?.spAutoModeSelection)
              ? undefined
              : unitData?.spAutoModeSelection === 1,
          )}
          type={booleanStatusToTagType(
            unitData?.spAutoModeSelection === 1,
            edgeStatus?.edgeStatus ? edgeStatus?.edgeStatus : null,
            unitData?.timestamp,
          )}
        />
      ),
    },
    {
      hasLargePadding: true,
      sensor: t('view.crusher.maintenance.setPoint.automation.feederMaximumSpeedDescription'),
      setpoint: formatValueWithUnit(unitData?.spMaxFeederSpeed, '%'),
    },
    {
      hasLargePadding: true,
      sensor: t('view.crusher.maintenance.setPoint.automation.cavityLevelKPDescription'),
      setpoint: formatValueWithUnit(unitData?.spPidAutoLevelFeederSpeedKp, ''),
    },
    {
      hasLargePadding: true,
      sensor: t('view.crusher.maintenance.setPoint.automation.cavityLevelKiDescription'),
      setpoint: formatValueWithUnit(unitData?.spPidAutoLevelFeederSpeedKi, ''),
    },
    {
      hasLargePadding: true,
      sensor: t('view.crusher.maintenance.setPoint.automation.cavityLevelKdDescription'),
      setpoint: formatValueWithUnit(unitData?.spPidAutoLevelFeederSpeedKd, ''),
    },
    {
      isBold: true,
      sensor: t('view.crusher.maintenance.setPoint.automation.currentControlTitle'),
    },
    {
      hasLargePadding: true,
      sensor: t('view.crusher.maintenance.setPoint.automation.crusherCurrentDescription'),
      setpoint: formatValueWithUnit(unitData?.spCrusherMotorAutoAmps, 'A'),
    },
    {
      hasLargePadding: true,
      sensor: t('view.crusher.maintenance.setPoint.automation.crusherCurrentLowDescription'),
      setpoint: formatValueWithUnit(unitData?.spCrusherMotorAutoAmpsLowLimit, 'A'),
    },
    {
      hasLargePadding: true,
      sensor: t('view.crusher.maintenance.setPoint.automation.crusherCurrentHighDescription'),
      setpoint: formatValueWithUnit(unitData?.spCrusherMotorAutoAmpsHighLimit, 'A'),
    },
    {
      isBold: true,
      sensor: t('view.crusher.maintenance.setPoint.automation.cavityLevelControlTitle'),
    },
    {
      hasLargePadding: true,
      sensor: t('view.crusher.maintenance.setPoint.automation.cavityLevelSensorDistanceDescription'),
      setpoint: formatValueWithUnit(unitData?.spFeedCavityLevel, '%'),
    },
    {
      hasLargePadding: true,
      sensor: t('view.crusher.maintenance.setPoint.automation.cavityLevelSensorHigh'),
      setpoint: formatValueWithUnit(unitData?.spFeedCavityLevelHighLimit, '%'),
    },
    {
      hasLargePadding: true,
      sensor: t('view.crusher.maintenance.setPoint.automation.lowCurrentRespTimeDescription'),
      setpoint: formatValueWithUnit(unitData?.spLowAmpsRespTime, 's'),
    },
  ];

  return (
    <Container className={className} data-testid={testId}>
      <ListContainer>
        <ListCol xs={24} lg={12}>
          <ListModule
            title={t('view.crusher.maintenance.setPoint.automation.autoBowlTitle')}
            items={autoBowlItems}
          />
        </ListCol>
        {unitData?.cavityLevelSensorEnable && (
          <ListCol xs={24} lg={12}>
            <ListModule
              title={t('view.crusher.maintenance.setPoint.automation.autoFeederTitle')}
              items={autoFeederItems}
            />
          </ListCol>
        )}
      </ListContainer>
    </Container>
  );
}

export { Automation };
